import { UseKycMeByOriginIdWithOriginProps } from './props';
import { useUserOriginId } from '../../user-identity/useUserOriginId';
import { useKycMeByOriginId } from '../../../api';
import { Nullable } from '@starsoft/common/interfaces';

/**
 * Custom hook to fetch KYC information by origin ID.
 * This hook combines the user's origin ID with an external origin ID (if provided)
 * and uses the combined origin ID to fetch KYC information.
 *
 * @param {UseKycMeByOriginIdWithOriginProps} [props] - Optional properties for fetching KYC information.
 * @param {boolean} [props.enabled=true] - Flag to enable or disable the query.
 * @param {string} [props.externalOriginId] - External origin ID to use if user origin ID is not available.
 * @returns {Object} - The result of the KYC query.
 *
 * Example usage:
 * const kycQuery = useKycMeByOriginIdWithOrigin({
 *   enabled: true,
 *   externalOriginId: '123',
 * });
 */
export function useKycMeByOriginIdWithOrigin(
  props?: UseKycMeByOriginIdWithOriginProps,
) {
  // Destructure enabled and externalOriginId from props, with default values
  const { enabled = true, externalOriginId } = props ?? {};

  // Get the user's origin ID, falling back to externalOriginId if not available
  const originId: Nullable<string> =
    useUserOriginId() ?? externalOriginId?.toString();

  // Use the combined origin ID to fetch KYC information
  return useKycMeByOriginId({ originId, enabled });
}

export type { UseKycMeByOriginIdWithOriginProps };
